/**
 * RandomColors Part
 * Randomizes colors on the Render part at runtime
 */

import { Part } from '../Part';
import { Events } from '../types';
import { Render } from './Render';
import { random } from '@/utils/Random';

/**
 * RandomColors part
 * On object creation, picks random colors from comma-separated lists
 * Supports: MainColor, DetailColor (and potentially others)
 */
export class RandomColors extends Part {
  readonly name = 'RandomColors';

  private mainColor?: string;
  private detailColor?: string;

  constructor(params: Record<string, unknown> = {}) {
    super();
    if (params.MainColor !== undefined) this.mainColor = String(params.MainColor);
    if (params.DetailColor !== undefined) this.detailColor = String(params.DetailColor);
  }

  onEvent(event: string, _data?: unknown): boolean {
    if (event === Events.OBJECT_CREATED) {
      this.applyRandomColors();
    }
    return true;
  }

  private applyRandomColors(): void {
    const render = this.parent?.getPart<Render>('Render');
    if (!render) return;

    // Pick random detail color from comma-separated list
    if (this.detailColor) {
      const colors = this.detailColor.split(',').map((c) => c.trim());
      if (colors.length > 0) {
        render.detailColor = random.choice(colors);
      }
    }

    // Pick random main color (affects colorString foreground)
    if (this.mainColor) {
      const colors = this.mainColor.split(',').map((c) => c.trim());
      if (colors.length > 0) {
        const fg = random.choice(colors);
        // Preserve background if present
        const parts = render.colorString.split('^');
        render.colorString = `&${fg}` + (parts[1] ? `^${parts[1]}` : '');
      }
    }
  }
}

/**
 * Factory for creating RandomColors parts
 */
export function createRandomColors(params: Record<string, unknown>): RandomColors {
  return new RandomColors(params);
}
